import { Truck } from '@numeo/types-core'
import { FLEET_TRUCK_PAGE_LIMIT, FleetTruckPage } from '@numeo/types-main-web-app'
import { ApiError, requestApi } from './axiosBaseQuery'

/**
 * Trucks for ids, asked one id at a time and sent together: every id asked within `BATCH_WINDOW_MS` goes into one
 * `GET /fleet/trucks/v1?ids=&includeDeleted=true` (at most 100 ids per request). A table that shows each row's truck costs
 * one request, not one per row. Deleted trucks come back too, by name, for the history that still points at them.
 */

const BATCH_WINDOW_MS = 25

type TruckResult = { data: Truck | null } | { error: ApiError }
type Resolve = (result: TruckResult) => void

let queue = new Map<string, Resolve[]>()
let scheduled = false

const send = async (ids: string[], waiting: Map<string, Resolve[]>): Promise<void> => {
    const result = await requestApi<FleetTruckPage>({
        url: '/fleet/trucks/v1',
        params: { ids: ids.join(','), includeDeleted: 'true', limit: FLEET_TRUCK_PAGE_LIMIT },
    })
    if ('error' in result) {
        for (const id of ids) for (const resolve of waiting.get(id) ?? []) resolve({ error: result.error })
        return
    }
    const found = new Map((result.data?.items ?? []).map((truck) => [String(truck._id), truck]))
    // An id the company has no truck for is cached as none, not asked again each render.
    for (const id of ids) for (const resolve of waiting.get(id) ?? []) resolve({ data: found.get(id) ?? null })
}

const flush = (): void => {
    const waiting = queue
    queue = new Map()
    scheduled = false
    const ids = [...waiting.keys()]
    for (let start = 0; start < ids.length; start += FLEET_TRUCK_PAGE_LIMIT) void send(ids.slice(start, start + FLEET_TRUCK_PAGE_LIMIT), waiting)
}

/** The truck with this id, sent with every other id asked in the same tick. Never rejects: a failure is an `error`. */
export const loadTruck = (id: string): Promise<TruckResult> =>
    new Promise((resolve) => {
        queue.set(id, [...(queue.get(id) ?? []), resolve])
        if (scheduled) return
        scheduled = true
        setTimeout(flush, BATCH_WINDOW_MS)
    })
